import React, { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Checkbox } from '../ui/checkbox';
import { Download, Check, EyeOff } from 'lucide-react';
import { applyAiqCustomer, importAiqCustomers, linkAiqCustomer, ignoreAiqCustomer } from '../../lib/api';
import { CUSTOMER_STATUS, errMsg } from './aiqMeta';
import { StatusBadge, StatusChips, DiffList } from './SyncBits';
import { LinkCodePopover } from './LinkCodePopover';

const rowKey = (r) => r.customer_id || `aiq-${r.code}`;

const CustomerActions = ({ row, busy, run }) => {
  if (row.status === 'differs') {
    return (
      <Button size="sm" variant="outline" className="h-7 text-xs" disabled={busy}
        onClick={() => run(() => applyAiqCustomer(row.customer_id, row.diffs.map((d) => d.field)), 'Updated from AccountsIQ')}
        data-testid={`cust-apply-${row.customer_id}`}>
        <Check className="w-3 h-3 mr-1" /> Use AccountsIQ details
      </Button>
    );
  }
  if (row.status === 'aiq_only') {
    return (
      <div className="flex gap-1.5 justify-end">
        <Button size="sm" variant="outline" className="h-7 text-xs" disabled={busy}
          onClick={() => run(() => importAiqCustomers([row.code]), `${row.name} added to the app`)}
          data-testid={`cust-import-${row.code}`}>
          <Download className="w-3 h-3 mr-1" /> Import
        </Button>
        <Button size="sm" variant="ghost" className="h-7 text-xs" disabled={busy}
          onClick={() => run(() => ignoreAiqCustomer(row.code), 'Ignored')} data-testid={`cust-ignore-${row.code}`}>
          <EyeOff className="w-3 h-3" />
        </Button>
      </div>
    );
  }
  if (row.status === 'not_in_aiq' || row.status === 'no_code') {
    return (
      <LinkCodePopover initial={row.code} label={row.code ? 'Fix code' : 'Add code'} busy={busy}
        onSave={(code) => run(() => linkAiqCustomer(row.customer_id, code), `Linked to ${code}`)}
        testId={`cust-link-${row.customer_id}`} />
    );
  }
  return null;
};

const CustomerRow = ({ row, selected, onSelect, busy, run }) => (
  <tr className="hover:bg-zinc-50/70 align-top" data-testid={`cust-row-${rowKey(row)}`}>
    <td className="px-3 py-2 w-8">
      {row.status === 'aiq_only' && (
        <Checkbox checked={selected} onCheckedChange={onSelect} data-testid={`cust-select-${row.code}`} />
      )}
    </td>
    <td className="px-3 py-2 text-sm">{row.customer_id ? row.name : <span className="text-zinc-400">—</span>}</td>
    <td className="px-3 py-2 font-mono text-xs">{row.code || '—'}</td>
    <td className="px-3 py-2 text-sm">{row.aiq?.name || <span className="text-zinc-400">—</span>}
      {row.aiq?.on_hold && <span className="ml-1.5 text-[10px] text-red-700 font-semibold">ON HOLD</span>}
    </td>
    <td className="px-3 py-2"><StatusBadge meta={CUSTOMER_STATUS} status={row.status} testId={`cust-status-${rowKey(row)}`} /></td>
    <td className="px-3 py-2">{row.diffs.length ? <DiffList diffs={row.diffs} /> : null}</td>
    <td className="px-3 py-2 text-right"><CustomerActions row={row} busy={busy} run={run} /></td>
  </tr>
);

export const CustomerSyncTable = ({ data, onChanged }) => {
  const [filter, setFilter] = useState('all');
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState([]);
  const [busy, setBusy] = useState(false);
  const [limit, setLimit] = useState(150);

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (data?.rows || []).filter((r) => (filter === 'all' || r.status === filter)
      && (!q || [r.name, r.code, r.aiq?.name].some((v) => (v || '').toLowerCase().includes(q))));
  }, [data, filter, query]);

  const run = async (fn, success) => {
    setBusy(true);
    try {
      await fn();
      toast.success(success);
      setSelected([]);
      await onChanged();
      return true;
    } catch (e) {
      toast.error(errMsg(e, 'Action failed'));
      return false;
    } finally {
      setBusy(false);
    }
  };

  const toggle = (code, on) => setSelected((s) => (on ? [...s, code] : s.filter((c) => c !== code)));

  return (
    <div className="space-y-3" data-testid="aiq-customer-review">
      <p className="text-xs text-zinc-500">AccountsIQ is the master. Customers are matched on the exact customer code (Accounts reference). Nothing changes until you choose an action.</p>
      <div className="flex flex-wrap items-center gap-3 justify-between">
        <StatusChips meta={CUSTOMER_STATUS} counts={data?.counts} value={filter} onChange={setFilter} testPrefix="cust" />
        <div className="flex gap-2">
          <Input placeholder="Search name or code…" value={query} onChange={(e) => setQuery(e.target.value)} className="h-8 w-56" data-testid="cust-search" />
          {selected.length > 0 && (
            <Button size="sm" className="h-8" disabled={busy} onClick={() => run(() => importAiqCustomers(selected), `${selected.length} customer(s) imported`)} data-testid="cust-import-selected">
              <Download className="w-3.5 h-3.5 mr-1" /> Import selected ({selected.length})
            </Button>
          )}
        </div>
      </div>
      <div className="border rounded-md overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="bg-zinc-50 text-[11px] uppercase text-zinc-600 text-left">
              <th className="px-3 py-2" /><th className="px-3 py-2">App customer</th><th className="px-3 py-2">Code</th>
              <th className="px-3 py-2">AccountsIQ name</th><th className="px-3 py-2">Status</th>
              <th className="px-3 py-2">Differences (app → AccountsIQ)</th><th className="px-3 py-2 text-right">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-100">
            {rows.slice(0, limit).map((r) => (
              <CustomerRow key={rowKey(r)} row={r} busy={busy} run={run}
                selected={selected.includes(r.code)} onSelect={(on) => toggle(r.code, on)} />
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={7} className="px-3 py-8 text-center text-sm text-zinc-500" data-testid="cust-empty">Nothing here.</td></tr>
            )}
          </tbody>
        </table>
      </div>
      {rows.length > limit && (
        <Button variant="ghost" size="sm" onClick={() => setLimit((l) => l + 150)} data-testid="cust-show-more">Show more ({rows.length - limit} left)</Button>
      )}
    </div>
  );
};
